import moment from 'moment-timezone';
import {
	OpeningHoursWithExceptions,
	StartTimeCount,
	OpeningHourTimesWithMoments,
} from 'common/types';

import { getNewEndTime } from 'common/utils/dateCalculations';
import { AvailabilityRange } from 'common/api/frontend/inventory_new/availabilityRange';
import {
	AvailabilityWithReason,
	isStartTimeSlotType,
	TimeSlotType,
	ValidityWithReason,
} from './types';
import { getOverlappingAvailabilityRanges, isStartTimeOverOrderLimit } from './utils';
import { getOpeningTimesForDateWithMoments } from 'common/utils/shopUtils';

export const getAvailabilityForTimeSlotWithReason = (args: {
	timeWithReason: { day: string; time: string; validity: ValidityWithReason };
	timeSlotType: TimeSlotType;
	openingHours: OpeningHoursWithExceptions;
	openingTimesByDate: Map<string, OpeningHourTimesWithMoments | null>;
	orderLimit: number;
	startTimeCounts: StartTimeCount;
	availabilityRanges: AvailabilityRange[];
	availabilityRangesToCompareFirst?: AvailabilityRange[];
	inventoryBlockerMinutesBefore?: number;
	inventoryBlockerMinutesAfter?: number;
	usesFixedTimesOutsideOpeningHours: boolean;
	alreadySelected?: number;
}): {
	availability: AvailabilityWithReason;
	overlappingRanges?: AvailabilityRange[];
} => {
	const {
		timeWithReason,
		timeSlotType,
		openingHours,
		availabilityRanges,
		availabilityRangesToCompareFirst,
	} = args;
	const { start, end } = getStartEndTime({ timeWithReason, timeSlotType, openingHours });

	const startTimeWithInventoryBlockers = start
		.clone()
		.subtract(args.inventoryBlockerMinutesBefore, 'minutes')
		.toISOString();
	const endDateWithInventoryBlockers = end
		.clone()
		.add(args.inventoryBlockerMinutesAfter, 'minutes')
		.toISOString();
	const relevantAvailabilityRanges = getAvailabilityRangesToUse({
		availabilityRanges,
		availabilityRangesToCompareFirst,
		endDateWithInventoryBlockers,
	});
	const overlappingRanges = getOverlappingAvailabilityRanges({
		startTime: startTimeWithInventoryBlockers,
		endTime: endDateWithInventoryBlockers,
		availabilityRanges: relevantAvailabilityRanges,
	});
	const overlappingRangeUnits = overlappingRanges?.map((r) => r.units ?? Infinity);
	const availableUnits =
		(overlappingRangeUnits?.length ? Math.min(...overlappingRangeUnits) : 0) -
		(args.alreadySelected ?? 0);

	// Check if units are over the start time limit
	if (
		isStartTimeSlotType(args.timeSlotType) &&
		isStartTimeOverOrderLimit(start, args.orderLimit, args.startTimeCounts)
	) {
		const availability: AvailabilityWithReason = {
			valid: false,
			reason: 'order_limit',
			units: availableUnits,
		};
		return { availability };
	}

	// Check if end date would go over the store end time
	const endDateString = end.format('YYYY-MM-DD');
	const endDateOpeningTimes =
		args.openingTimesByDate.get(endDateString) ??
		getOpeningTimesForDateWithMoments(end, args.openingHours);
	if (!args.usesFixedTimesOutsideOpeningHours) {
		const unavailableOpeninghours: AvailabilityWithReason = {
			units: availableUnits,
			valid: false,
			reason: 'opening_hours',
		};
		if (!endDateOpeningTimes) return { availability: unavailableOpeninghours };
		if (!args.openingTimesByDate.has(endDateString)) {
			args.openingTimesByDate.set(endDateString, endDateOpeningTimes);
		}
		const { openMoment, closeMoment, isClosed: endDateIsClosed } = endDateOpeningTimes;
		const endTimeWithinOpeningHours = end.isBetween(openMoment, closeMoment, undefined, '[]');
		if (!endTimeWithinOpeningHours || endDateIsClosed) {
			return { availability: unavailableOpeninghours };
		}
	}
	const availability: AvailabilityWithReason =
		availableUnits > 0
			? { ...args.timeWithReason.validity, units: availableUnits }
			: {
					valid: false,
					reason: !args.timeWithReason.validity.valid
						? args.timeWithReason.validity.reason
						: 'availability',
					units: availableUnits,
			  };
	return { availability, overlappingRanges };
};

const getStartEndTime = (args: {
	timeWithReason: { day: string; time: string; validity: ValidityWithReason };
	timeSlotType: TimeSlotType;
	openingHours: OpeningHoursWithExceptions;
}) => {
	const timeSlotMoment = moment(`${args.timeWithReason.day}T${args.timeWithReason.time}`);
	if (isStartTimeSlotType(args.timeSlotType)) {
		return {
			start: timeSlotMoment,
			end: moment(
				getNewEndTime(
					timeSlotMoment.toISOString(),
					{
						durationInSeconds: args.timeSlotType.selectedDuration?.durationInSeconds || 1, //Fixed product rentals might not have duration,
						durationType: args.timeSlotType.selectedDuration?.durationType ?? '24h',
						durationName: null,
					},
					args.openingHours,
				),
			),
		};
	} else {
		return {
			start: args.timeSlotType.startTime,
			end: timeSlotMoment,
		};
	}
};

const getAvailabilityRangesToUse = (args: {
	availabilityRangesToCompareFirst?: AvailabilityRange[];
	availabilityRanges: AvailabilityRange[];
	endDateWithInventoryBlockers: string;
}) => {
	const {
		availabilityRangesToCompareFirst,
		availabilityRanges,
		endDateWithInventoryBlockers,
	} = args;
	let relevantAvailabilityRanges: AvailabilityRange[] = availabilityRanges;
	if (!!availabilityRangesToCompareFirst?.length) {
		const lastRange = availabilityRangesToCompareFirst[availabilityRangesToCompareFirst.length - 1];
		if (endDateWithInventoryBlockers < lastRange.end) {
			relevantAvailabilityRanges = availabilityRangesToCompareFirst;
		}
	}
	return relevantAvailabilityRanges;
};
